import type { HTMLProps, PropsWithChildren } from 'react';
import type {
  FieldValues,
  SubmitErrorHandler,
  SubmitHandler,
  UseFormReturn,
} from 'react-hook-form';
import { FormProvider } from 'react-hook-form';
import type { z } from 'zod';

import type { ComponentProps } from '../ComponentProps';
import { FormSchemaProvider } from './FormSchemaProvider';

export interface FormProps<FormType extends FieldValues>
  extends PropsWithChildren,
    ComponentProps<
      Omit<HTMLProps<HTMLFormElement>, 'method' | 'onError' | 'onSubmit'>
    > {
  formHook: UseFormReturn<FormType>;
  onError?: SubmitErrorHandler<FormType>;
  onSubmit: SubmitHandler<FormType>;
  values?: FormType;
  schema: z.ZodTypeAny;
}

export function Form<FormType extends FieldValues>({
  children,
  formHook,
  onError,
  onSubmit,
  schema,
  ...formProps
}: FormProps<FormType>) {
  return (
    <FormSchemaProvider schema={schema}>
      <FormProvider {...formHook}>
        <form
          {...formProps}
          onSubmit={formHook.handleSubmit(onSubmit, onError)}
          className="flex w-full grow flex-col justify-between gap-md"
        >
          {children}
        </form>
      </FormProvider>
    </FormSchemaProvider>
  );
}

Form.displayName = 'Form';
